<template>
  <div class="px-3">
    <v-dialog
      v-model="showInviteUser"
      max-width="400px"
    >
      <videon-card heading="Invite User" showClose="true" @close="closeInviteUserDialog">
        <v-card-text>
          <v-form @submit.prevent="inviteUser">
            <p class="mt-3">
              Please enter the email address of the user you wish to invite.
            </p>
            
            <v-text-field
              v-model="inviteUserEmail"
              label="E-mail"
              type="email"
              :error-messages="emailErrors"
              @input="$v.inviteUserEmail.$touch()"
              @blur="$v.inviteUserEmail.$touch()"
              class="mb-2 mt-2"
              required
              outlined
            ></v-text-field>
            
            <v-select
              v-model="newUserPermissions"
              :items="[{'text': 'Read Only', 'value': 100}, {'text': 'User', 'value': 200}, {'text': 'Admin', 'value': 300}]"
              label="Access Level"
              outlined
            ></v-select>
            
            
            <div class="text-right">
              <v-btn
                class="ma-1"
                color="secondary"
                @click="closeInviteUserDialog"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="inviteUser"
              >
                Invite
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <v-dialog
      v-model="showEditPermissions"
      max-width="400px"
    >
      <videon-card heading="Edit Fleet Permissions" showClose="true" @close="showEditPermissions = false">
        <v-card-text>
          <v-form @submit.prevent="editPermsForUser">
            <user-label v-if="editPermsUser" :user="$users.getUser(editPermsUser.user_guid)" class="mb-6 mt-2"/>
            
            <v-select
              v-model="newUserPermissions"
              :items="[{'text': 'Read Only', 'value': 100}, {'text': 'User', 'value': 200}, {'text': 'Admin', 'value': 300}]"
              label="Access Level"
              outlined
            ></v-select>
            
            
            <div class="text-right">
              <v-btn
                class="ma-1"
                color="secondary"
                @click="showEditPermissions = false"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="editPermsForUser"
              >
                Update
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <v-card flat>
      <v-card-title>
        {{ fleet.users.length }} Fleet Users
        <v-spacer/>
        <v-btn
          small
          color="primary"
          outlined
          @click="openInviteUserDialog"
          tooltip="Invite User"
          v-if="fleet.canEdit()"
        >
          <v-icon>mdi-plus</v-icon> Invite User
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert dense outlined type="error" v-if="userError">
          {{ userErrorText }}
        </v-alert>
  
        <v-data-table
          :headers="userHeaders"
          :items="fleet.users"
          :loading="loading"
          hide-default-footer
          disable-pagination
          :items-per-page="200"
        >
          <template v-slot:item.user_guid="{ item }">
            <user-label :user="$users.getUser(item.user_guid)" />
          </template>
          <template v-slot:item.access="{ item }">
           <access-level-label :access_level="item.access" />
          </template>
          <template v-slot:item.actions="{ item }">
            <div v-if="fleet.canEdit() && !item.org_guid">
              <v-btn
                class="ma-1"
                x-small
                outlined
                color="secondary"
                @click="editUser(item)"
              >
                Edit Permissions
              </v-btn>
              <v-btn
                class="ma-1"
                x-small
                outlined
                @click="deleteUser(item)"
              >
                Remove User
              </v-btn>
            </div>
            <div v-if="item.org_guid">
              <v-btn
                class="ma-1"
                x-small
                outlined
                disabled
              >
                Managed by Org
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions v-if="hasUserChanges">
        <v-row
           align="center"
           justify="end"
           class="ma-2"
         >
           <v-btn
             color="secondary"
             @click="revertChanges"
             class="ma-1"
             outlined
           >
             Revert
           </v-btn>
           <v-btn
             color="primary"
             @click="saveChanges"
             class="ma-1"
           >
             Save
           </v-btn>
         </v-row>
      </v-card-actions>
    </v-card>

    <v-card flat v-if="fleet.userInvites.length > 0">
      <v-card-title>
        Pending Invites
        <v-spacer/>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="userHeaders"
          :items="fleet.userInvites"
          hide-default-footer
          :items-per-page="200"
        >
          <template v-slot:item.user_guid="{ item }">
            <span class="subtitle-2">{{ item.target_email }}</span>
          </template>
          <template v-slot:item.access="{ item }">
            <access-level-label :access_level="item.access" />
          </template>
          <template v-slot:item.actions="{ item }">
            <div v-if="fleet.canEdit()" class="text-right">
              <v-btn
                class="ma-1"
                x-small
                outlined
                @click="deleteInvite(item)"
              >
                Cancel Invite
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
  import { mapGetters } from 'vuex'
  
  import { validationMixin } from 'vuelidate'
  import { required, email } from 'vuelidate/lib/validators'
  
  export default {
    name: 'FleetUsers',
    
    mixins: [validationMixin],
    
    props: ['fleet_guid', 'fleet'],
    
    validations: {
      inviteUserEmail: { required, email },
    },
    
    data() {
      return {
        loading: false,
        
        showFleetUsers: false,
        userError: false,
        userErrorText: '',
      
        showInviteUser: false,
        inviteUserEmail: '',
        
        showEditPermissions: false,
        editPermsUser: false,
        newUserPermissions: 100,
        
        hasUserChanges: false,
      
        userHeaders: [
          { text: 'User', align: 'start', value: 'user_guid', sortable: false },
          { text: 'Access Level', align: 'center', value: 'access', sortable: false },
          { text: '', align: 'end', value: 'actions', sortable: false },
        ],
        
        invitesAddExisting: false,
      }
    },
    computed: {
      ...mapGetters('user', ['user', 'guid', 'isOrgAdmin']),
      
      emailErrors () {
        const errors = []
        if (!this.$v.inviteUserEmail.$dirty) return errors
        !this.$v.inviteUserEmail.email && errors.push('Must be valid e-mail')
        !this.$v.inviteUserEmail.required && errors.push('E-mail is required')
        return errors
      },
      
    },
    
    mounted() {
      this.fleet.fetchUserInvites()
    },
    
    methods: {
      openInviteUserDialog() {
        this.userError = false
        this.userErrorText = ''
        this.inviteUserEmail = ''
        this.newUserPermissions = 100
        if (this.$v && this.$v.inviteUserEmail) {
          this.$v.inviteUserEmail.$reset()
        }
        this.showInviteUser = true
      },

      closeInviteUserDialog() {
        this.showInviteUser = false
        this.userError = false
        this.userErrorText = ''
      },

      editUser(item) {
        this.editPermsUser = item
        this.newUserPermissions = item.access
        this.showEditPermissions = true
      },
      
      editPermsForUser() {
        this.showEditPermissions = false
        for (var x in this.fleet.users) {
          if (this.fleet.users[x].user_guid == this.editPermsUser.user_guid) {
             // eslint-disable-next-line 
            this.fleet.users[x].access = this.newUserPermissions
            this.hasUserChanges = true
            return
          }
        }
        
      },
      
      deleteUser(item) {
        this.loading = true
      
        this.axios.delete('/fleets/' + this.fleet_guid + '/users', {'data': {'users': [item.user_guid]}})
        .then((response) => {
          console.log('FleetUsers deleteUser response', response)
          
          this.fleet.fetchUsers()
        }).catch((error) => {
          console.log('FleetUsers deleteUser error', error)
        
          this.error = true
          this.errorText = this.$helpers.parseError(error)
        
          this.loading = false
        })
      },
      
      saveChanges() {
        this.loading = true
        
        var updateUsers = []
        for (var x in this.fleet.users) {
          // dont include org users....
          if (!this.fleet.users[x].org_guid) {
            updateUsers.push({'user_guid': this.fleet.users[x].user_guid, 'access': this.fleet.users[x].access})
          }
        }
        
        console.log('FleetUsers saveChanges users', updateUsers)
        
        this.axios.put('/fleets/' + this.fleet_guid + '/users', { 'users': updateUsers })
        .then((response) => {
          console.log('FleetUsers saveChanges response', response)
          this.hasUserChanges = false
          
          this.fleet.fetchUsers()
          
          this.loading = false
        }).catch((error) => {
          console.log('FleetUsers saveChanges error', error)
          this.error = true
          this.errorText = this.$helpers.parseError(error)
          this.loading = false
        })
      },
      
      revertChanges() {
        this.fleet.fetchUsers()
      },
      
      inviteUser() {
        this.userError = false
        this.userErrorText = ''
        
        if (this.inviteUserEmail) {
          const normalizedInviteEmail = String(this.inviteUserEmail).trim().toLowerCase()
          console.log('FleetUsers inviteUser', normalizedInviteEmail)
          this.showInviteUser = false
          
          this.loading = true
          
          for (var x in this.fleet.users) {
            var fleetUser = this.fleet.users[x]
            var resolvedUser = this.$users.getUser(fleetUser.user_guid)
            const resolvedEmail = resolvedUser && resolvedUser.email
              ? String(resolvedUser.email).trim().toLowerCase()
              : ''
            if (resolvedEmail && resolvedEmail === normalizedInviteEmail) {
              // Allow overriding org-inherited membership by creating a direct
              // fleet assignment with the requested access level.
              if (fleetUser.org_guid) {
                this.axios.post('/fleets/' + this.fleet_guid + '/users', {
                  'user_guid': fleetUser.user_guid,
                  'access': this.newUserPermissions
                })
                .then((response) => {
                  console.log('FleetUsers inviteUser existing-org-user response', response)
                  this.fleet.fetchUsers()
                  this.loading = false
                  this.inviteUserEmail = ''
                }).catch((error) => {
                  console.log('FleetUsers inviteUser existing-org-user error', error)
                  this.userError = true
                  this.userErrorText = this.$helpers.parseError(error)
                  this.loading = false
                })
                return
              }

              this.userError = true
              this.userErrorText = 'A user with this email already exists in this fleet.'
              this.loading = false
              return
            }
          }
          
          this.axios.post('/invites', {'fleet_guid': this.fleet_guid, 'target_email': normalizedInviteEmail, 'access': this.newUserPermissions})
          .then((response) => {
            console.log('FleetUsers inviteUser response', response)
            
            this.fleet.fetchUserInvites()
            this.loading = false
            
            this.inviteUserEmail = ''
          }).catch((error) => {
            console.log('FleetUsers inviteUser error', error)
            this.userError = true
            this.userErrorText = this.$helpers.parseError(error)
            this.loading = false
          })
        }
      },
      
      deleteInvite(item) {
        if (item.invite_guid) {
          this.loading = true
      
          this.axios.delete('/invites/' + encodeURIComponent(item.invite_guid), {'data': {'fleet_guid': this.fleet_guid}})
          .then((response) => {
            console.log('FleetUsers deleteInvite response', response)
            
            this.fleet.fetchUserInvites()
            
            this.loading = false
          }).catch((error) => {
            console.log('FleetUsers deleteInvite error', error)
            this.userError = true
            this.userErrorText = this.$helpers.parseError(error)
            this.loading = false
          })
        }
      },
      
    }
  }
</script>